import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Stats from "@/components/Stats";
import Wedding from "@/components/Wedding";
import Pricing from "@/components/Pricing";
import Services from "@/components/Services";
import VideoShowcase from "@/components/VideoShowcase";
import VideoGallery from "@/components/VideoGallery";
import About from "@/components/About";
import FAQ from "@/components/FAQ";
import BlogPreview from "@/components/BlogPreview";
import BlogList from "@/components/BlogList";
import BlogPost from "@/components/BlogPost";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";

/**
 * Old URL paths (from previous version of drones3x.fr) → new section anchors.
 * Keeps existing Google rankings by redirecting old URLs to the same content
 * via React Router. Each redirect also sets a canonical "/" so Google
 * consolidates ranking signals on the home page.
 */
const LEGACY_REDIRECTS = {
  // Pricing / tarifs
  "/tarifs": "/#pricing",
  "/tarif": "/#pricing",
  "/prix": "/#pricing",
  "/prix-spectacle-drones": "/#pricing",
  "/tarif-spectacle-drones": "/#pricing",
  // Wedding
  "/mariage": "/#mariage",
  "/mariages": "/#mariage",
  "/spectacle-drones-mariage": "/#mariage",
  // Services
  "/services": "/#services",
  "/prestations": "/#services",
  "/corporate": "/#services",
  "/festivals": "/#services",
  "/sport": "/#services",
  // About
  "/a-propos": "/#about",
  "/about": "/#about",
  "/qui-sommes-nous": "/#about",
  // Contact
  "/contact": "/#contact",
  "/devis": "/#contact",
  // FAQ
  "/faq": "/#faq",
  "/questions": "/#faq",
  // Video / showreel
  "/showreel": "/#showreel",
  "/videos": "/#showreel",
};

const LegacyRedirect = ({ to }) => {
  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://drones3x.fr/";
  }, []);
  return <Navigate to={to} replace />;
};

const Landing = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Spectacle de drones en France & Europe — Entreprise française drones3x.fr";
  }, []);

  // Smooth-scroll to hash target on arrival (after redirect from /tarifs etc.)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [location]);

  return (
    <div className="App">
      <Navbar />
      <main>
        <Hero />
        <Stats />
        <Services />
        <Wedding />
        <Pricing />
        <VideoShowcase />
        <VideoGallery />
        <About />
        <FAQ />
        <BlogPreview />
        <Contact />
      </main>
      <Footer />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a0c",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {Object.entries(LEGACY_REDIRECTS).map(([oldPath, target]) => (
          <React.Fragment key={oldPath}>
            <Route path={oldPath} element={<LegacyRedirect to={target} />} />
            <Route path={`${oldPath}/`} element={<LegacyRedirect to={target} />} />
          </React.Fragment>
        ))}
        <Route path="*" element={<LegacyRedirect to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
